/**
 * Workflow Draft Service
 *
 * Integrates with backend Redis draft system for autosave functionality.
 * Data-driven: all workflow data autosaved to Redis, then persisted to DynamoDB.
 * Life-critical: prevents data loss, maintains audit trails.
 *
 * Architecture:
 * - Redis: Temporary drafts with TTL (autosave every N seconds)
 * - DynamoDB: Permanent workflows when explicitly saved
 * - GraphQL: saveDraft mutation, getDraft query, submitWorkflow mutation
 */

import { ApolloClient, gql } from '@apollo/client';

/**
 * Draft data structure (Redis)
 * Temporary storage with TTL for autosave
 */
export interface WorkflowDraft {
  tenant: string;
  kind: string; // 'workflow-design'
  key: string; // Unique draft key (e.g., workflow ID or session ID)
  data: {
    // Workflow design data
    nodes: Array<{
      id: string;
      type: string;
      name: string;
      x: number;
      y: number;
      metadata: Record<string, any>;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      metadata?: Record<string, any>;
    }>;
    // Workflow metadata
    workflowMetadata?: {
      name?: string;
      description?: string;
      version?: string;
    };
  };
  created_at: string;
  updated_at: string;
  created_by: string;
  expires_at?: string;
}

/**
 * Saved workflow structure (DynamoDB)
 * Permanent storage after explicit save
 */
export interface SavedWorkflow {
  id: string;
  name: string;
  description?: string;
  version: string;
  status: 'draft' | 'active' | 'archived' | 'deprecated';
  // Workflow definition (stored as JSONB in DynamoDB)
  definition: {
    nodes: Array<{
      id: string;
      type: string;
      name: string;
      position: { x: number; y: number };
      data: Record<string, any>;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      label?: string;
      metadata?: Record<string, any>;
    }>;
  };
  // Audit trail
  created_by: string;
  created_at: string;
  updated_at: string;
  updated_by?: string;
  // Compliance
  compliance_flags?: string[];
  requires_approval?: boolean;
  // Organization
  tags?: string[];
  tenant: string;
}

// Draft storage uses localStorage until backend draft API is implemented.
// This eliminates GraphQL errors from missing getDraft/saveDraft resolvers
// while preserving the same interface for autosave functionality.
const DRAFT_STORAGE_PREFIX = 'ferp_draft_';

const SAVE_WORKFLOW_MUTATION = gql`
  mutation SaveWorkflow($input: SaveWorkflowInput!) {
    saveWorkflow(input: $input) {
      id
      name
      version
      status
      definition
      created_by
      created_at
      updated_at
    }
  }
`;

const LIST_WORKFLOWS_QUERY = gql`
  query ListWorkflows($tenant: String!, $status: String) {
    listWorkflows(tenant: $tenant, status: $status) {
      items {
        id
        name
        description
        version
        status
        created_by
        created_at
        updated_at
        compliance_flags
        tags
      }
      total
    }
  }
`;

/**
 * Workflow Draft Service
 */
export class WorkflowDraftService {
  private client: ApolloClient;
  private tenant: string;
  private autosaveIntervalMs: number;
  private draftTTLSeconds: number;
  private autosaveTimer?: number;

  constructor(
    client: ApolloClient,
    tenant: string,
    options: {
      autosaveIntervalMs?: number;
      draftTTLSeconds?: number;
    } = {},
  ) {
    this.client = client;
    this.tenant = tenant;
    this.autosaveIntervalMs = options.autosaveIntervalMs || 5000; // 5 seconds
    this.draftTTLSeconds = options.draftTTLSeconds || 86400; // 24 hours
  }

  /**
   * Save draft to localStorage (autosave)
   * Called automatically every N seconds.
   * Uses localStorage until backend draft API (getDraft/saveDraft) is implemented.
   */
  async saveDraft(draftKey: string, data: WorkflowDraft['data'], actor: string): Promise<void> {
    try {
      const storageKey = `${DRAFT_STORAGE_PREFIX}${this.tenant}_${draftKey}`;
      const draft: WorkflowDraft = {
        tenant: this.tenant,
        kind: 'workflow-design',
        key: draftKey,
        data,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: actor,
      };
      localStorage.setItem(storageKey, JSON.stringify(draft));
      console.log('[WorkflowDraftService] Draft autosaved to localStorage:', draftKey);
    } catch (error) {
      console.error('[WorkflowDraftService] Failed to save draft:', error);
      // Don't throw - autosave should be non-blocking
    }
  }

  /**
   * Get draft from localStorage
   * Called on page load to restore unsaved work.
   */
  async getDraft(draftKey: string): Promise<WorkflowDraft | null> {
    try {
      const storageKey = `${DRAFT_STORAGE_PREFIX}${this.tenant}_${draftKey}`;
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      return JSON.parse(raw) as WorkflowDraft;
    } catch (error) {
      console.error('[WorkflowDraftService] Failed to load draft:', error);
      return null;
    }
  }

  /**
   * Delete draft from localStorage
   * Called after successful save to DynamoDB.
   */
  async deleteDraft(draftKey: string): Promise<void> {
    try {
      const storageKey = `${DRAFT_STORAGE_PREFIX}${this.tenant}_${draftKey}`;
      localStorage.removeItem(storageKey);
      console.log('[WorkflowDraftService] Draft deleted from localStorage:', draftKey);
    } catch (error) {
      console.error('[WorkflowDraftService] Failed to delete draft:', error);
    }
  }

  /**
   * Start autosave (call when component mounts)
   */
  startAutosave(draftKey: string, getData: () => WorkflowDraft['data'], actor: string): () => void {
    this.autosaveTimer = window.setInterval(() => {
      const data = getData();
      // Only autosave if there's actual data
      if (data.nodes.length > 0 || data.edges.length > 0) {
        this.saveDraft(draftKey, data, actor);
      }
    }, this.autosaveIntervalMs);

    // Return cleanup function
    return () => {
      if (this.autosaveTimer) {
        clearInterval(this.autosaveTimer);
        this.autosaveTimer = undefined;
      }
    };
  }

  /**
   * Save workflow to DynamoDB (permanent storage)
   * Called when user explicitly clicks "Save"
   */
  async saveWorkflow(input: {
    id?: string;
    name: string;
    description?: string;
    version: string;
    status?: string;
    definition: SavedWorkflow['definition'];
    actor: string;
    complianceFlags?: string[];
    tags?: string[];
  }): Promise<SavedWorkflow> {
    const result = await this.client.mutate<Record<string, any>>({
      mutation: SAVE_WORKFLOW_MUTATION,
      variables: {
        input: {
          id: input.id,
          tenant: this.tenant,
          name: input.name,
          description: input.description,
          version: input.version,
          status: input.status || 'draft',
          definition: input.definition,
          compliance_flags: input.complianceFlags,
          tags: input.tags,
        },
      },
      context: {
        headers: {
          'x-actor': input.actor,
        },
      },
    });

    return result.data?.saveWorkflow ?? null;
  }

  /**
   * List workflows from DynamoDB
   */
  async listWorkflows(filters?: { status?: string }): Promise<SavedWorkflow[]> {
    const result = await this.client.query<Record<string, any>>({
      query: LIST_WORKFLOWS_QUERY,
      variables: {
        tenant: this.tenant,
        status: filters?.status,
      },
    });

    return result.data?.listWorkflows?.items || [];
  }

  /**
   * Delete workflow from DynamoDB
   */
  async deleteWorkflow(workflowId: string, actor: string): Promise<void> {
    const DELETE_MUTATION = gql`
      mutation DeleteWorkflow($tenant: String!, $id: String!) {
        deleteWorkflow(tenant: $tenant, id: $id)
      }
    `;

    await this.client.mutate<Record<string, any>>({
      mutation: DELETE_MUTATION,
      variables: {
        tenant: this.tenant,
        id: workflowId,
      },
      context: {
        headers: {
          'x-actor': actor,
        },
      },
    });
  }
}

/**
 * Generate stable draft key for workflow session
 * Uses workflow ID if exists, otherwise generates session-based key
 */
export function generateDraftKey(workflowId?: string, sessionId?: string): string {
  if (workflowId) {
    return `workflow_${workflowId}`;
  }
  if (sessionId) {
    return `workflow_session_${sessionId}`;
  }
  // Fallback: generate random key
  return `workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
